import { LowerCasePipe } from '../../filters/lower-case.pipe';
import { CurrencyPipe } from '../../filters/currency.pipe';
import { TranslatePipe } from '../../filters/translate.pipe';
import { LoaderOverlayComponent } from '../../components/loader-overlay/loader-overlay.component';
import { ListBanklinksComponent } from '../../components/list-banklinks/list-banklinks.component';
import { CardServiceComponent } from '../../components/card-service/card-service.component';
import { ListLanguageFlagsComponent } from '../../components/list-language-flags/list-language-flags.component';
import { ItemNotificationComponent } from '../../components/item-notification/item-notification.component';
import { Component, OnInit, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import RoutingService from '../../services/routing.service';
import AngularFilterHelperService from '../../services/angular-filter-helper.service';
import BookingService from '../../services/booking.service';
import UserRestServices from '../../services/user-rest.service';
import BusinessRestServices from '../../services/business-rest.service';
import Config from '../../utils/config.service';
import Session from '../../utils/session.service';
import ServiceRestServices from '../../services/service-rest.service';
import UrlUtil from '../../utils/url-util.service';
import CategoryService from '../../services/category.service';
import UserService from '../../services/user.service';
import AngularHelperService from '../../services/angular-helper.service';
import AngularRoutingHelperService from '../../services/angular-routing-helper.service';
import AngularBroadcastHelperService from '../../services/angular-broadcast-helper.service';
import moment from 'moment';
import { PanelDirective } from '../../directives/panel.directive';
import { Subscription } from 'rxjs';

declare const Stripe: any;

@Component({
	standalone: true,
	selector: 'booking-checkout',
	templateUrl: './booking-checkout.component.html',
	imports: [
		CommonModule, PanelDirective, LowerCasePipe, CurrencyPipe, 
		TranslatePipe, LoaderOverlayComponent, ListBanklinksComponent, 
		CardServiceComponent, ListLanguageFlagsComponent, RouterLink, 
		ItemNotificationComponent, ReactiveFormsModule, FormsModule
	]
})
export default class BookingCheckoutView implements OnInit, OnDestroy {
	private routingService: RoutingService = inject(RoutingService);
	private angularFilterHelperService: AngularFilterHelperService = inject(AngularFilterHelperService);
	private bookingService: BookingService = inject(BookingService);
	private userRestServices: UserRestServices = inject(UserRestServices);
	private businessRestServices: BusinessRestServices = inject(BusinessRestServices);
	private config: Config = inject(Config);
	private session: Session = inject(Session);
	private serviceRestServices: ServiceRestServices = inject(ServiceRestServices);
	private urlUtil: UrlUtil = inject(UrlUtil);
	private categoryService: CategoryService = inject(CategoryService);
	private userService: UserService = inject(UserService);
	private angularHelperService: AngularHelperService = inject(AngularHelperService);
	private angularRoutingHelperService: AngularRoutingHelperService = inject(AngularRoutingHelperService);
	private angularBroadcastHelperService: AngularBroadcastHelperService = inject(AngularBroadcastHelperService);
	
	vm: any = undefined;
	walletSubscription: Subscription = undefined;
	watcher: any = undefined;
	mainForm: FormGroup;
	
	constructor(private fb: FormBuilder) {
		this.vm = this;
		this.mainForm = this.fb.group({
			termsCheckbox: [false, Validators.requiredTrue],
			ppCheckbox: [false, Validators.requiredTrue],
			dataProcessingCheckbox: [false],
			dataStorageTimeCheckbox: [false],
			dataSharingCheckbox: [false],
		});
	}
	
	ngOnInit() {
		this.routingService.validateAccess('booking').then(() => {
			if (this.angularRoutingHelperService.getCurrentPath() === 'booking/prescription/checkout') {
				var rawData: any = window.sessionStorage.getItem(this.config.getBookingConfig().Instant.STORAGE_KEY);
				if (rawData) {
					this.vm.rawBooking = JSON.parse(rawData);
				}
				if (!this.vm.rawBooking) {
					this.angularRoutingHelperService.setPath([''], false, true);
					return;
				}
				this.loadOndemandSettings();
			} else if (this.angularRoutingHelperService.getCurrentPath() === 'booking/instant/checkout') {
				var rawData: any = window.sessionStorage.getItem(this.config.getBookingConfig().Instant.STORAGE_KEY);
				if (rawData) {
					this.vm.rawBooking = JSON.parse(rawData);
				}
				if (!this.vm.rawBooking) {
					this.angularRoutingHelperService.setPath([''], false, true);
					return;
				}
				this.loadOndemandSettings();
			} else {
				this.preloadScheduledBooking();
			}
		});
	}

	ngOnDestroy() {
		if (this.walletSubscription) {
			this.walletSubscription.unsubscribe();
		}
		if (this.vm.expiryInterval) this.angularHelperService.cancelInterval(this.vm.expiryInterval);
		if (this.watcher) {
			this.angularHelperService.cancelInterval(this.watcher);
		}
	}
	
	preloadScheduledBooking() {
		var serviceId: any = this.angularRoutingHelperService.getCurrentRouteParam('serviceId');
		var params: any = this.angularRoutingHelperService.getQueryParams() || {};
		
		if (params['bookingId']) {
			this.serviceRestServices.getBookingDetails({ bookingId: params['bookingId'] },
				(booking) => {
					if (booking.service.customerContactAddressRequired) {
						this.userRestServices.getContactInfo((contact) => {
							contact = contact || {};
							var isContactValid: any = !!contact.country 
								// && !!contact.county 
								&& !!contact.city && !!contact.address && !!contact.zipCode;
							if (isContactValid) {
								this.vm.rawBooking = booking;
								this.onResolve();
							} else {
								this.angularRoutingHelperService.setPath(['booking/service/', serviceId, 'address'], false, true);
							}
						});
					} else {
						this.vm.rawBooking = booking;
						this.onResolve();
					}
				},
				(error) => {
					console.error(error);
				}
			);
		} else {
			this.bookingService.getUnfinishedBooking(Number(serviceId))
				.then((booking) => {
					if (!booking) {
						this.angularRoutingHelperService.setPath(['booking/service/', serviceId], false, true);
					} else if (booking.service.customerContactAddressRequired) {
						this.userRestServices.getContactInfo((contact) => {
							contact = contact || {};
							var isContactValid: any = !!contact.country 
								// && !!contact.county
								&& !!contact.city && !!contact.address && !!contact.zipCode;
							if (isContactValid) {
								this.vm.rawBooking = booking;
								this.onResolve();
							} else {
								this.angularRoutingHelperService.setPath(['booking/service/', serviceId, 'address'], false, true);
							}
						});
					} else {
						this.vm.rawBooking = booking;
						this.onResolve();
					}
				});
		}
	}
	
	loadOndemandSettings() {
		var rawData: any = window.sessionStorage.getItem(this.config.getBookingConfig().Instant.STORAGE_KEY);
		var booking: any = rawData ? JSON.parse(rawData) : {};
		
		if (!booking) {
			this.angularRoutingHelperService.setPath('/', false, true);
		} else {
			if (!booking.service) {
				var params: any = {
					origin: booking.origin
				};
				this.businessRestServices.getOnDemandSettings(params, (settings) => {
					this.vm.onDemandSettings = settings;
					this.onResolve();
				});
			} else {
				this.vm.onDemandSettings = [];
				this.onResolve();
			}
		}
	}
	
	onResolve() {
		this.vm.gdprDisclaimerTranslated = this.angularFilterHelperService.translate('Modal.Content.Consents_Gdpr_Disclaimer');
		var params: any = this.angularRoutingHelperService.getQueryParams() || {};
		if (params['bookingId']) {
			this.vm.bookingId = params['bookingId'];
		}
		this.vm.serviceId = this.angularRoutingHelperService.getRouteParam('serviceId');
		this.vm.user = this.session.getSession().user;
		this.vm.isOpen = { service: true, consents: true };
		this.vm.businessWalletErrors = {};
		this.vm.avTestUrl = this.urlUtil.createAvTestUrl();

		this.handleBookingInit(this.vm.rawBooking);
		this.userService.clearWalletCache();
		this.getConsents();
		
		var currentSession = this.session.getSession();
		this.watcher = this.angularHelperService.createInterval(() => {
			var newSession = this.session.getSession();
			if (!this.angularHelperService.isEqual(newSession, currentSession)) {
				this.handleSessionChange(newSession, currentSession);
				currentSession = Object.assign({}, newSession);
			}
		}, 300);
		
		//this.angularStateHelperService.scopeWF(this, this.userService.getWallet, this.handleWalletChange, true);
		this.walletSubscription = this.angularBroadcastHelperService.onEvent(this, "walletChanged", (event, newWallet) => {
			this.handleWalletChange(newWallet);
		});
		this.userService.getWallet(); // TODO ANGULAR_2: should this be inside an interval?
		
		this.vm.handleDiscountCheck = this.handleDiscountCheck;
		this.vm.handleFormSubmit = this.handleFormSubmit;
		// this.vm.handlePaymentAndSubmit = handlePaymentAndSubmit;
		this.vm.handleBanklinkPayment = this.handleBanklinkPayment;
		this.vm.handlePayment = this.handlePayment;
		this.vm.isStripeEnabled = this.isStripeEnabled;
		this.vm.getBusinessWalletLabel = this.getBusinessWalletLabel;
		this.vm.getInsuranceCoPayInfo = this.getInsuranceCoPayInfo;
		this.vm.getInsuranceCoPayInfoWithClaimsNotSupported = this.getInsuranceCoPayInfoWithClaimsNotSupported;
		this.vm.getWalletDisabledForThirdPersonWarning = this.getWalletDisabledForThirdPersonWarning;
		this.vm.getInsuranceWalletDisabledForInvalidPeriodWarning = this.getInsuranceWalletDisabledForInvalidPeriodWarning;
		
		this.vm.initialized = true;
	}
	
	getPreparedDataForRequest(isUnpaid?) {
		var data: any = {
			platform: 'BROWSER',
			origin: this.vm.booking.origin,
			subcategory: this.vm.booking.subcategory,
			furtherSubcategory: this.vm.booking.furtherSubcategory,
			partnerRefCode: window.sessionStorage.getItem("LOC")
		};
		if (this.vm.booking.careTakenInfo) {
			data.careTakenInfo = this.vm.booking.careTakenInfo;
		}
		if (this.vm.booking.prolongedRequestAllowed) {
			data.prolongedRequestAllowed = this.vm.booking.prolongedRequestAllowed;
		}
		
		if (isUnpaid) {
			data.furtherSubcategory = this.vm.booking.furtherSubcategory;
		} else {
			data.consents = this.getConsentsUpdated();
		}
		
		if (this.vm.booking.interview) {
			data.interview = this.vm.booking.interview;
		}
		
		if (this.vm.isPrescription) {
			data.type = this.vm.booking.type;
			data.prescriptionRequest = this.vm.booking.prescriptionRequest;
		} else {
			data.comment = this.vm.booking.comment;
			data.languages = this.vm.booking.languages.join(',');
			data.communicationType = this.vm.booking.communicationType;
		}
		
		return data;
	}
	
	handleSessionChange(newSess, oldSess) {
		if (this.angularHelperService.equals(newSess, oldSess) || !newSess) return;
		this.vm.user = newSess.user;
	}
	
	handleWalletChange(newWallet) {
		if (this.angularHelperService.equals(newWallet, this.vm.balance)) return;
		
		this.vm.balance = newWallet || {};

		this.calculateLeftToPay();
		this.updateBusinessWalletErrors(() => {
			if (!this.angularHelperService.equals(this.vm.balance, {})) this.handlePaymentMethodsInit();
		});
	}
	
	handleBookingInit(booking) {
		this.vm.thirdPartyRenewal = window.sessionStorage.getItem("THIRD_PARTY_RENEWAL");
		var params: any = this.angularRoutingHelperService.getQueryParams() || {};
		this.vm.booking = booking.service ? this.handleBookingServicePreparation(booking) : this.handleBookingInstantPreparation(booking);
		
		this.vm.isHomeVisit = this.vm.booking.service && this.vm.booking.service.subcategory === 'HOME_VISITS';
		this.vm.isPrescription = this.vm.booking.type === 'PRESCRIPTION_REQUEST';
		
		if (this.vm.booking.subcategory) this.getCategory();
		if (this.vm.booking.bookingStart && !params['bookingId']) {
			this.startExpiryTimer(new Date(this.vm.booking.bookingStart));
		} else {
			this.updateCheckboxStates(false);
			this.vm.mode = 'RESERVATION_BOOKING'
		}
	}
	
	handleBookingServicePreparation(booking) {
		booking.service.pricing = booking.service.pricing || booking.price || {};
		booking.price = booking.service.pricing.price;
		booking.currency = this.angularFilterHelperService.currency(booking.service.pricing.currency);
		booking.service.userRating = booking.service.userRating || booking.userRating;
		booking.date = moment(booking.scheduledStart).locale(this.vm.user.deviceLanguage.slice(0, 2)).format('dddd, D MMM YYYY');
		booking.start = moment(booking.scheduledStart).format('HH:mm');
		booking.end = moment(booking.scheduledEnd).format('HH:mm');
		booking.duration = moment.duration(moment(booking.scheduledEnd).diff(moment(booking.scheduledStart))).asMinutes();
		if (this.vm.bookingId) {
			booking.backUrl = ['/booking', this.vm.bookingId, 'reservation'];
		} else {
			if (booking.service.customerContactAddressRequired) {
				booking.backUrl = ['/booking/service', this.vm.serviceId, 'address'];
			} else {
				booking.backUrl = ['/booking/service', this.vm.serviceId];
			}
		}
		
		if (booking.isCoveredByBusinessWallet) {
			this.vm.canPayWithBusinessWallet = true;
		}
		
		return booking;
	}
	
	handleBookingInstantPreparation(booking) {
		var bookingType: any = booking.type || booking.subcategory;
		var settings;
		
		switch (bookingType) {
			case 'PRESCRIPTION_REQUEST':
				if (this.vm.thirdPartyRenewal === 'benu' && window.sessionStorage.getItem("LOC")) {
					settings = this.vm.onDemandSettings.filter((item) => {
						return item.subcategory === 'GENERAL_MEDICAL_PRACTICE' && item.furtherSubcategory === 'BENU_PRESCRIPTION_QR';
					})[0] || {};
					booking.furtherSubcategory = settings.furtherSubcategory;
				} else if (this.vm.thirdPartyRenewal === 'benu') {
					settings = this.vm.onDemandSettings.filter((item) => {
						return item.subcategory === 'GENERAL_MEDICAL_PRACTICE' && item.furtherSubcategory === 'BENU_PRESCRIPTION';
					})[0] || {};
					booking.furtherSubcategory = settings.furtherSubcategory;
				} else {
					settings = this.vm.onDemandSettings.filter((item) => {
						return item.subcategory === 'GENERAL_MEDICAL_PRACTICE' && !item.furtherSubcategory;
					})[0] || {};
					settings.furtherSubcategory = ['GENERAL_PRACTITIONER', 'FAMILY_PHYSICIAN'];
					booking.generalPractitionerSpecialCheck = 'prescriptionsOnly';
				}

				booking.subcategory = settings.subcategory;
				booking.price = typeof settings.price !== 'undefined'
					? settings.price
					: this.config.getBookingConfig().Instant.Prescription.DEFAULT_PRICE;
				booking.currency = this.config.getBookingConfig().Instant.Prescription.DEFAULT_CURRENCY;
				booking.duration = this.config.getBookingConfig().Instant.Prescription.DEFAULT_DURATION_MINUTES;
				booking.backUrl = ['/booking/prescription'];

				break;
			case 'PHARMACIST':
				settings = this.vm.onDemandSettings.filter((item) => {
					return item.subcategory === 'PHARMACIST' && item.furtherSubcategory === 'PHARMACIST';
				})[0] || {};

				booking.price = typeof settings.price !== 'undefined'
					? settings.price
					: this.config.getBookingConfig().Instant.Pharmacist.DEFAULT_PRICE;
				booking.currency = this.angularFilterHelperService.currency(this.config.getBookingConfig().Instant.Pharmacist.DEFAULT_CURRENCY);
				booking.duration = typeof settings.timePerPrice !== 'undefined'
					? settings.timePerPrice
					: this.config.getBookingConfig().Instant.Pharmacist.DEFAULT_DURATION_MINUTES;
				booking.backUrl = ['/booking/instant'];
				booking.queryParams = {category: booking.subcategory};
				booking.subcategory = settings.subcategory;
				booking.furtherSubcategory = settings.furtherSubcategory;

				break;
			default:
				settings = this.vm.onDemandSettings.filter((item) => {
					return item.subcategory === 'GENERAL_MEDICAL_PRACTICE' && item.furtherSubcategory === 'FAMILY_PHYSICIAN';
				})[0] || {};
				booking.generalPractitionerSpecialCheck = 'gpOnly';

				booking.price = typeof settings.price !== 'undefined'
					? settings.price
					: this.config.getBookingConfig().Instant.Doctor.DEFAULT_PRICE;
				booking.currency = this.angularFilterHelperService.currency(this.config.getBookingConfig().Instant.Doctor.DEFAULT_CURRENCY);
				booking.duration = typeof settings.timePerPrice !== 'undefined'
					? settings.timePerPrice
					: this.config.getBookingConfig().Instant.Doctor.DEFAULT_DURATION_MINUTES;
				booking.backUrl = ['/booking/instant'];
				booking.queryParams = {category: booking.subcategory};
				booking.subcategory = settings.subcategory;
				booking.furtherSubcategory = settings.furtherSubcategory;

				break;
		}
		
		var walletFetchInput: any = {
			pricing: { price: booking.price },
			furtherSubcategory: settings.furtherSubcategory,
			generalPractitionerSpecialCheck: booking.generalPractitionerSpecialCheck
		};
		if (booking.careTakenInfo) {
			walletFetchInput.careTakenPersonalCode = booking.careTakenInfo.personalCode;
			if (booking.service) {
				booking.service.careTakenPersonalCode = booking.careTakenInfo.personalCode;
			}
		}
		walletFetchInput.scheduledStart = booking.scheduledStart;
		this.userService.getWalletWithPrice(walletFetchInput)
			.then((usedWallet) => {
				this.processUsedWallet(booking.price, booking, usedWallet);
			})
			.catch((e) => {
				console.error(e);
			});
		
		if (booking.languages) booking.languages = booking.languages.slice();
		booking.date = moment().locale(this.vm.user.deviceLanguage.slice(0, 2)).format('dddd, D MMM YYYY');
		
		return booking;
	}
	
	getCategory() {
		var furtherSubcategories: any = { GENERAL_MEDICAL_PRACTICE: 'FAMILY_PHYSICIAN' };
		this.categoryService.getAll().then((categories) => {
			this.vm.booking.category = categories.filter((category) => {
				return category.subcategory === this.vm.booking.subcategory
					&& category.furtherSubcategory === furtherSubcategories[this.vm.booking.subcategory];
			})[0];
		});
	}
	
	calculateLeftToPay() {
		this.vm.leftToPay = this.vm.booking.price ? this.vm.booking.price - (this.vm.balance.balance || 0) : -1;
	}
	
	isExtraConsentsRequired() {
		return this.vm.user.origin == 'siffi.com'
			|| (this.vm.booking && this.vm.booking.service && !this.vm.booking.service.minudocExtraConsentsDisabled);
	}
	
	getConsents() {
		var extendedConsentsRequired = this.isExtraConsentsRequired();
		
		if (extendedConsentsRequired) {
			this.mainForm.get('dataProcessingCheckbox')?.setValidators(Validators.requiredTrue);
			this.mainForm.get('dataStorageTimeCheckbox')?.setValidators(Validators.requiredTrue);
			this.mainForm.get('dataSharingCheckbox')?.setValidators(Validators.requiredTrue);
			this.vm.dataProcessingAcceptedVisible = true;
			this.vm.dataStorageTimeAcceptedVisible = true;
			this.vm.dataSharingAcceptedVisible = true;
			this.vm.gdprDisclaimerVisible = false;
		} else {
			this.vm.dataProcessingAcceptedVisible = false;
			this.vm.dataStorageTimeAcceptedVisible = false;
			this.vm.dataSharingAcceptedVisible = false;
			this.vm.gdprDisclaimerVisible = true;
		}

		if (!this.vm.booking.consents) {
			this.vm.booking.consents = {};
			this.vm.booking.consents.dataProcessingAccepted = this.vm.dataProcessingAcceptedVisible ? false : null;
			this.vm.booking.consents.dataStorageTimeAccepted = this.vm.dataStorageTimeAcceptedVisible ? false : null;
			this.vm.booking.consents.dataSharingAccepted = this.vm.dataSharingAcceptedVisible ? false : null;
		} else {
			this.vm.booking.consents.terms = !!this.vm.booking.consents.id;
		}
		this.vm.links = {};
		if (this.vm.user.origin == 'minudoc.ee') {
			this.vm.links.tos = this.urlUtil.createPublicUrl({
				'et_EE': '/tto/kasutustingimused',
				'en_GB': '/hsp/terms-of-service',
				'ru_RU': '/pmu/uslovija-ispolzovanija',
				'fi_FI': '/tpt/kayttoehdot'
			});
			this.vm.links.pp = this.urlUtil.createPublicUrl({
				'et_EE': '/tto/privaatsuspoliis',
				'en_GB': '/hsp/privacy-policy',
				'ru_RU': '/pmu/politika-konfidencialnosti',
				'fi_FI': '/tpt/tietosuojakaytanto'
			});
		} else {
			var locale: any = (this.vm.user && this.vm.user.deviceLanguage) || 'en_GB';
			this.vm.links.tos = this.config.getSiffiPublicWebUrl(locale, '/hsp/terms-of-service');
			this.vm.links.pp = this.config.getSiffiPublicWebUrl(locale, '/hsp/privacy-policy');
		}
		this.vm.termsConsent = this.angularHelperService.trustAsHtml(
			this.angularFilterHelperService.translate('Modal.Content.AgreeTo')
				.replace('%s', '<a href="' + this.vm.links.tos +'" target="_blank">' + this.angularFilterHelperService.translate('Modal.Content.ToS') + '</a>')
		);
		this.vm.ppConsent = this.angularHelperService.trustAsHtml(
			this.angularFilterHelperService.translate('Modal.Content.HaveRead')
				.replace('%s', '<a href="' + this.vm.links.pp +'" target="_blank">' + this.angularFilterHelperService.translate('Modal.Content.PrivacyPolicy') + '</a>')
		);
	}
	
	handlePaymentMethodsInit() {
		if (this.vm.canPayWithBusinessWallet || this.vm.leftToPay <= 0) return;
		if (this.isStripeEnabled()) {
			this.handleStripeInit();
		} else if (!this.vm.booking.service) {
			this.handleInstantBookingPaymentMethodsInit();
		}
	}
	
	isStripeEnabled() {
		if (!this.vm.user.personalCode) {
			return true;
		} else if (this.vm.user.origin == 'minudoc.ee') {
			return false;
		} else {
			return true;
		}
	}
	
	handleStripeInit() {
		this.loadScript('https://js.stripe.com/v3/', 'stripe-js')
			.then(() => {
				// give some time for the browser to load Stripe JS
				setTimeout(() => {
					var stripe: any = Stripe(this.config.getStripePublishedKey());

					var privateWalletCompleteButton: any = document.querySelector('#privateWalletCompleteButton');
					if (privateWalletCompleteButton) {
						privateWalletCompleteButton.disabled = true;
					}
					var paymentInput: any = {};
					paymentInput.origin = this.vm.booking.origin;
					if (this.vm.booking.service) {
						paymentInput.booking = this.vm.booking;
						// TODO SEN-1584 let backend figure out real price
						paymentInput.price = {};
						paymentInput.price.price = this.vm.leftToPay;
						paymentInput.price.currency = this.vm.booking.service.pricing.currency;
					} else {
						// booking instant request logic
						paymentInput.subcategory = this.vm.booking.subcategory;
						paymentInput.furtherSubcategory = this.vm.booking.furtherSubcategory;
					}

					var configParams: RequestInit = {
						method: 'POST',
						credentials: 'include',
						headers: {
							'Content-Type': 'application/json'
						},
						body: JSON.stringify(paymentInput)
					};
					var fetchUrl = this.vm.booking.service
						? this.config.getRestUrl() + 'rest/payment/stripe/create/intent/booking'
						: this.config.getRestUrl() + 'rest/payment/stripe/create/intent/booking/instant/request';
					fetch(fetchUrl, configParams)
						.then((result) => {
							return result.json();
						})
						.then((data) => {
							this.vm.stripePayment = data;
							var elements: any = stripe.elements();
							var style: any = {
								base: {
									color: '#212121',
									fontFamily: 'Titillium Web, sans-serif',
									fontSmoothing: 'antialiased',
									fontSize: '15px',
									'::placeholder': {
										color: '#212121'
									}
								},
								invalid: {
									fontFamily: 'Titillium Web, sans-serif',
									color: '#fa755a',
									iconColor: '#fa755a'
								}
							};
							var card: any = elements.create('card', { style: style });
							// Stripe injects an iframe into the DOM
							card.mount('#card-element');
							card.on('change', (event) => {
								var cardErrorEl: any = document.querySelector('#card-error');
								if (cardErrorEl) {
									cardErrorEl.textContent = event.error ? event.error.message : '';
								}
								var completeButton: any = document.querySelector('#privateWalletCompleteButton');
								if (event.error) {
									this.vm.creditCardDataIncomplete = true;
									if (completeButton) {
										completeButton.disabled = true;
									}
								} else {
									this.vm.creditCardDataIncomplete = false;
									if (completeButton) {
										completeButton.disabled = false;
									}
								}
							});
							this.vm.payWithCreditCard = () => {
								this.vm.submitted = true;
								if (!this.mainForm.valid || this.vm.isBookingExpired) {
									this.mainForm.markAllAsTouched();
									this.vm.isOpen.consents = true;
									return;
								}
								if (this.vm.creditCardDataIncomplete) return;

								this.vm.isProcessing = true;

								this.payWithCard(stripe, card, data.clientSecret);
							}
						});
				}, 500);
			})
			.catch(console.error);
	}
	
	// Calls stripe.confirmCardPayment
	// If the card requires authentication Stripe shows a pop-up modal to
	// prompt the user to enter authentication details without leaving your page.
	payWithCard(stripe, card, clientSecret) {
		stripe.confirmCardPayment(clientSecret, { payment_method: { card: card } })
			.then((result) => {
				console.log(result);
				if (result.error) {
					this.vm.isProcessing = false;
					this.showStripeError(result.error.message);
				} else {
					if (result.paymentIntent && result.paymentIntent.status == 'succeeded') {
						this.orderComplete('COMPLETED');
					} else {
						this.orderComplete('ERROR');
					}
				}
			});
	}
	
	orderComplete(status) {
		this.vm.stripePayment.status = status;
		this.vm.stripePayment.platform = 'BROWSER';
		var configParams: RequestInit = {
			method: 'PUT',
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(this.vm.stripePayment)
		};
		fetch(this.config.getRestUrl() + 'rest/payment/stripe/complete', configParams)
			.then((result) => {
				return result.json();
			})
			.then((data) => {
				if (status == 'COMPLETED') {
					this.completeBookingAfterStripePayment();
				} else {
					this.handleStripeInit();
					this.vm.isProcessing = false;
				}
			})
			.catch(console.error);
	}
	
	showStripeError(errorMsgText) {
		var errorMsgEl: any = document.querySelector('#card-error');
		if (errorMsgEl) {
			errorMsgEl.textContent = errorMsgText;
			setTimeout(() => {
				errorMsgEl.textContent = '';
			}, 4000);
		}
	}
	
	loadScript(src, id) {
		if (!src) {
			return this.angularHelperService.getPromise().reject('No src specified');
		}
		
		if (!id) {
			return this.angularHelperService.getPromise().reject('No id specified');
		}
		
		if (document.getElementById(id)) {
			console.info('LOAD_SCRIPT: script "' + src + '" is ignored, since it is already loaded');
			return this.angularHelperService.getPromise().resolve();
		}
		
		var deferred: any = this.angularHelperService.getDeferredPromise();
		var fjs: any = document.getElementsByTagName('script')[0];
		var js: any = document.createElement('script');
		js.id = id;
		js.src = src
		js.onload = () => {
			this.angularHelperService.createTimer(() => {
				deferred.resolve();
			}, 100);
		};
		fjs.parentNode.insertBefore(js, fjs);
		
		return deferred.promise;
	}
	
	handleInstantBookingPaymentMethodsInit() {
		var getBookingRequest: any = (this.vm.booking.id || !this.vm.booking.price)
			? this.angularHelperService.getPromise().resolve
			: this.createUnpaidBookingRequest;
		var successCallback = (res?) => {
			if (this.vm.booking.id || !res || !res.bookingInstantRequest || !res.bookingInstantRequest.id) return;
			if (!this.vm.booking.id) this.vm.banklinks = res.paymentMethods;
			this.vm.booking.id = res.bookingInstantRequest.id;
			this.updateBookingInStorage({ id: res.bookingInstantRequest.id });
		};
		var errorCallback = (err) => {
			console.error(err);
		};
		if (this.vm.booking.id || !this.vm.booking.price) {
			successCallback();
		} else {
			this.createUnpaidBookingRequest()
				.then(successCallback)
				.catch(successCallback);
		}
	}
	
	updateBookingInStorage(values) {
		var rawData: any = window.sessionStorage.getItem(this.config.getBookingConfig().Instant.STORAGE_KEY);
		var data: any = rawData ? JSON.parse(rawData) : {};
		Object.keys(values).forEach((key) => { data[key] = values[key]; });
		window.sessionStorage.setItem(this.config.getBookingConfig().Instant.STORAGE_KEY, JSON.stringify(data));
	}
	
	createUnpaidBookingRequest() {
		var deferred: any = this.angularHelperService.getDeferredPromise();
		var data: any = this.getPreparedDataForRequest(true);
		
		this.serviceRestServices.createUnpaidInstantBookingRequestWithPaymentMethods(data,
			(res) => {
				deferred.resolve(res);
			},
			(err) => {
				deferred.reject(err);
			}
		);
		
		return deferred.promise;
	}
	
	handleFormSubmit() {
		this.vm.submitted = true;
		if (!this.mainForm.valid || this.vm.isBookingExpired) {
			this.mainForm.markAllAsTouched();
			this.vm.isOpen.consents = true;
			return;
		}
		if (this.vm.isProcessing) return;
		this.vm.isProcessing = true;
		
		this.handleBookingSubmit().catch((err) => {
			this.userService.clearWalletCache();
			this.vm.isProcessing = false;
		});
	}
	
	handleDiscountCheck() {
		if (this.vm.isProcessing) return;
		this.vm.isProcessing = true;
		
		var checkedPricing: any = {};
		checkedPricing.service = {};
		checkedPricing.service.id = this.vm.booking.service.id;
		checkedPricing.discountCode = this.vm.discountCode;
		checkedPricing.bookingId = this.vm.booking.id;
		
		this.serviceRestServices.checkDiscount(checkedPricing,
			(res) => {
				this.vm.usingDiscountPrice = true;
				this.vm.discountError = false;
				this.vm.discountSuccess = true;
				this.vm.booking.price = res.price;
				this.vm.booking.service.pricing = res;
				this.calculateLeftToPay();
				this.updateBusinessWalletErrors(() => {
					if (res.price > 0) {
						this.vm.banklinksReloadNeeded = true;
						this.handlePaymentMethodsInit();
					}
					this.vm.isProcessing = false;
				});
			},
			(err) => {
				this.vm.discountError = true;
				this.vm.discountSuccess = false;
				this.vm.isProcessing = false;
				// TODO SEN-1755 display error
			}
		);
	}
	
	handleBookingSubmit() {
		var deferred: any = this.angularHelperService.getDeferredPromise();
		
		if (this.vm.booking.service) {
			var bookingData: any = { id: this.vm.booking.id, consents: this.getConsentsUpdated() };
			if (this.vm.usingDiscountPrice) {
				bookingData.price = this.vm.booking.service.pricing;
			}
			this.serviceRestServices.confirmBooking(bookingData,
				(res) => {
					this.angularRoutingHelperService.setPath('/booking/checkout/success');
					this.userService.clearWalletCache();
					deferred.resolve();
				},
				(err) => {
					deferred.reject(err);
				}
			);
		} else {
			var data: any = this.getPreparedDataForRequest();
			
			this.serviceRestServices.createInstantBookingRequest(data,
				(res) => {
					window.sessionStorage.removeItem(this.config.getBookingConfig().Instant.STORAGE_KEY);
					this.userService.clearWalletCache();
					this.angularRoutingHelperService.setPath('/booking/instant/' + res.id + '/pending');
					deferred.resolve();
				},
				(err) => {
					deferred.reject(err);
				}
			);
		}
		
		return deferred.promise;
	}
	
	handlePayment() {
		if (this.isStripeEnabled()) {
			this.vm.payWithCreditCard();
		} else {
			this.handleBanklinkPayment();
		}
	}
	
	completeBookingAfterStripePayment() {
		var updateBookingConsents: any = this.vm.booking.service
			? this.serviceRestServices.updateBookingConsents
			: this.serviceRestServices.updateInstantBookingConsents;
		
		window.sessionStorage.removeItem(this.config.getBookingConfig().Instant.STORAGE_KEY);
		this.handleBookingSubmit();
	}
	
	handleBanklinkPayment() {
		this.vm.submitted = true;
		if (!this.mainForm.valid || this.vm.isBookingExpired || !this.vm.banklink || this.vm.isProcessing || !this.vm.booking.id) {
			this.mainForm.markAllAsTouched();
			return;
		}
		
		this.vm.isProcessing = true;
		
		var banklink: any = this.vm.banklink;
		var params: any = Object.keys(banklink.parameters);
		
		var form: any = document.createElement('form');
		form.name = banklink.bankName;
		form.target = '_top';
		form.method = 'POST';
		form.action = banklink.serviceUrl;
		
		params.forEach((param) => {
			var input: any = document.createElement('input');
			input.type = 'hidden';
			input.name = param;
			input.value = banklink.parameters[param];
			form.appendChild(input);
		});
		
		var successCallback = () => {
			window.sessionStorage.removeItem(this.config.getBookingConfig().Instant.STORAGE_KEY);
			document.body.appendChild(form);
			form.submit();
			this.userService.clearWalletCache();
		};
		var errorCallback = (err) => {
			console.log(err);
		};
		
		if (this.vm.booking.service) {
			this.serviceRestServices.updateBookingConsents({ id: this.vm.booking.id, consents: this.getConsentsUpdated() }, 
				successCallback, errorCallback);
		} else {
			this.serviceRestServices.updateInstantBookingConsents({ id: this.vm.booking.id, consents: this.getConsentsUpdated() }, 
				successCallback, errorCallback);
		}

	}
	
	getConsentsUpdated() {
		var extendedConsentsRequired = this.isExtraConsentsRequired();

		this.vm.booking.consents = {};
		this.vm.booking.consents.terms = this.mainForm.get('termsCheckbox').value && this.mainForm.get('ppCheckbox').value;
		if (extendedConsentsRequired) {
			this.vm.booking.consents.dataProcessingAccepted = this.mainForm.get('dataProcessingCheckbox').value;
			this.vm.booking.consents.dataStorageTimeAccepted = this.mainForm.get('dataStorageTimeCheckbox').value;
			this.vm.booking.consents.dataSharingAccepted = this.mainForm.get('dataSharingCheckbox').value;
		}
		return this.vm.booking.consents;
	}
	
	startExpiryTimer(startedAt) {
		this.vm.expiryTimerStartedAt = moment(new Date(startedAt)).add(15 * 60, 'seconds');
		if (this.vm.expiryInterval) this.angularHelperService.cancelInterval(this.vm.expiryInterval);
		var newDisableState = moment() > this.vm.expiryTimerStartedAt;
		this.updateCheckboxStates(newDisableState);
		this.vm.bookingExpiresIn = moment() < this.vm.expiryTimerStartedAt
			? moment.utc(moment(this.vm.expiryTimerStartedAt).diff(moment())).format('mm:ss')
			: undefined;
		
		this.vm.expiryInterval = this.angularHelperService.createInterval(() => { this.checkExpiry(); }, 1000);
	}
	
	checkExpiry() {
		if (moment() > this.vm.expiryTimerStartedAt) {
			this.updateCheckboxStates(true);
			this.angularHelperService.cancelInterval(this.vm.expiryInterval);
		} else {
			this.vm.bookingExpiresIn = moment.utc(moment(this.vm.expiryTimerStartedAt).diff(moment())).format('mm:ss');
		}
	}
	
	updateCheckboxStates(disable) {
		if (disable !== this.vm.isBookingExpired) {
			this.vm.isBookingExpired = disable;
			if (disable) {
				this.mainForm.get('termsCheckbox').disable();
				this.mainForm.get('ppCheckbox').disable();
				this.mainForm.get('dataProcessingCheckbox').disable();
				this.mainForm.get('dataStorageTimeCheckbox').disable();
				this.mainForm.get('dataSharingCheckbox').disable();
			} else {
				this.mainForm.get('termsCheckbox').enable();
				this.mainForm.get('ppCheckbox').enable();
				this.mainForm.get('dataProcessingCheckbox').enable();
				this.mainForm.get('dataStorageTimeCheckbox').enable();
				this.mainForm.get('dataSharingCheckbox').enable();
			}
		}
	}
	
	updateBusinessWalletErrors(callback) {
		try {
			var servicePrice: any = this.vm.booking.service ? this.vm.booking.service.pricing.price : this.vm.booking.price;
			var subcategory: any = this.vm.booking.service
				? this.vm.booking.service.subcategory
				: (this.vm.booking.subcategory ? this.vm.booking.subcategory : this.vm.booking.category?.subcategory);
			var furtherSubcategory: any = this.vm.booking.service
				? this.vm.booking.service.furtherSubcategory
				: (this.vm.booking.furtherSubcategory
					? this.vm.booking.furtherSubcategory
					: (this.vm.booking.category ? this.vm.booking.category?.furtherSubcategory : undefined));
			var category: any = furtherSubcategory || subcategory;
		} catch (e) {
			console.error(e);
			callback();
			return;
		}
		
		if (servicePrice === null || servicePrice === undefined || !category) {
			callback();
			return;
		}
		
		var walletFetchInput: any = {
			pricing: { price: servicePrice },
			furtherSubcategory: category,
			generalPractitionerSpecialCheck: this.vm.booking.generalPractitionerSpecialCheck
		};
		if (this.vm.booking.careTakenInfo) {
			walletFetchInput.careTakenPersonalCode = this.vm.booking.careTakenInfo.personalCode;
			if (this.vm.booking.service) {
				this.vm.booking.service.careTakenPersonalCode = this.vm.booking.careTakenInfo.personalCode;
			}
		}
		walletFetchInput.scheduledStart = this.vm.booking.scheduledStart;
		this.userService.getWalletWithPrice(walletFetchInput)
			.then((usedWallet) => {
				this.processUsedWallet(servicePrice, this.vm.booking, usedWallet);
				callback();
			})
			.catch((e) => {
				console.error(e);
				callback();
			});
	}
	
	processUsedWallet(servicePrice, booking, usedWallet) {
		this.vm.businessWallet = {};
		booking.insuranceWallet = false;
		if (usedWallet.alternative) {
			if (usedWallet.coPayWallet) {
				booking.walletName = usedWallet.coPayWallet.wallet.name;
				booking.insuranceCoPay = true;
				booking.coPay = usedWallet.coPayWallet.wallet.coPay;
				booking.claimsNotSupported = usedWallet.coPayWallet.wallet.claimsNotSupported;
			} else {
				booking.walletName = usedWallet.wallet.name;
			}
			if (usedWallet.wallet.walletType == 'INSURANCE') {
				booking.insuranceWallet = true;
			}
		}
		
		this.vm.insuranceWalletDisabledForThirdPerson = usedWallet.insuranceWalletDisabledForThirdPerson;
		this.vm.insuranceWalletDisabledForInvalidPeriod = usedWallet.insuranceWalletDisabledForInvalidPeriod;
		
		booking.isCoveredByBusinessWallet = servicePrice && !usedWallet.price && !usedWallet.coPayWallet;
		if (booking.isCoveredByBusinessWallet) {
			this.vm.canPayWithBusinessWallet = true;
			this.vm.businessWallet = usedWallet.wallet;
		}
		this.vm.walletProcessed = true;
	}
	
	getBusinessWalletLabel() {
		if (this.vm.booking.insuranceCoPay) {
			return 'Services.Other.Insurance_Cover';
		} else if (this.vm.booking.walletName?.toLowerCase() === 'iizi') {
			return 'Services.Other.Package_By';
		} else {
			return 'Services.Other.Covered_By';
		}
	}
	
	getInsuranceCoPayInfo() {
		return this.angularFilterHelperService.translate('Services.Other.Insurance_Co_Pay_Info')
			.replace('%s$1', this.vm.booking.walletName)
			.replace('%s$2', this.vm.booking.coPay)
			.replace('%s$3', '%');
	}
	
	getInsuranceCoPayInfoWithClaimsNotSupported() {
		return this.angularFilterHelperService.translate('Services.Other.Insurance_Co_Pay_Info_With_Claims_Not_Supported')
			.replace('%s$1', this.vm.booking.walletName)
			.replace('%s$2', this.vm.booking.coPay)
			.replace('%s$3', '%');
	}
	
	getWalletDisabledForThirdPersonWarning() {
		return this.angularFilterHelperService.translate('Services.Other.Insurance_Not_Supported_For_Third_Person');
	}
	
	getInsuranceWalletDisabledForInvalidPeriodWarning() {
		return this.angularFilterHelperService.translate('Services.Other.Insurance_Wallet_Disabled_For_Invalid_Period');
	}
	
}
